import { Paper } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Typography, Button, LinearProgress, Grid } from "@material-ui/core";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@material-ui/core";

// Icons
import EditIcon from "@material-ui/icons/Edit";
import GetAppIcon from "@material-ui/icons/GetApp";
import DeleteIcon from "@material-ui/icons/Delete";
import BarChartIcon from "@material-ui/icons/BarChart";
import VisibilityIcon from "@material-ui/icons/Visibility";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";

// Connectors
import backendConnector from "../../../services/connectors/backendConnector";

// Components
import { DotFalling } from "../../loader/Loaders";

// Services
import sessionStorage from "../../../services/storage/SessionStorage";

// Styles
import HistoricalResultTableStyle from "../../../assets/styles/materialUI/questionnaire/assessmentResult/HistoricalResultTableStyle";

// Providers
import { useLanguage } from "../../../contexts/languageContext";

export default function HistoricalResultTable({
	setError,
	setMessage,
	setLoading,
	historyResult,
	handleViewAnswers,
	setSubmissionData,
	setDataLoading,
	setSelectedAssessment,
	setDashboardOpen,
	setDeleteConfirm,
	partialLoading,
	openRenameDialog,
}) {
	const history = useHistory();
	const { t } = useTranslation();
	const { language } = useLanguage();
	const classes = HistoricalResultTableStyle();

	const getStatusKey = (status) => {
		const normalized = (status || "").toLowerCase();
		if (normalized.includes("complete")) return "completed";
		if (normalized.includes("progress")) return "inProgress";
		return "notStarted";
	};

	const isCompleted = (status) => getStatusKey(status) === "completed";

	const getDashboardData = async (item) => {
		setError(null);
		setDataLoading(true);
		let submission_data = {
			submission_id: item?.id,
		};
		let response = await backendConnector.getSubmissionData(submission_data);
		if (response?.data) {
			setSelectedAssessment(item);
			setSubmissionData(response?.data);
			setDashboardOpen(true);
		} else if (response?.error) {
			setError(response?.error);
		}
		setDataLoading(false);
		setTimeout(() => {
			setError(null);
		}, 3000);
	};

	const handleOpenDashboard = (item) => {
		getDashboardData(item);
	};

	const handleDelete = (item) => {
		setSelectedAssessment(item);
		setDeleteConfirm(true);
	};

	const handleEdit = async (item) => {
		setLoading(true);
		sessionStorage.setPathName("session_id", item?.id);
		setTimeout(() => {
			setLoading(false);
			history.push("/questionnaire/assessment_form");
		}, 2000);
	};

	const downloadSubmissionDataExcel = async (item, fullLoading = false) => {
		setError(null);
		setLoading(fullLoading);
		setDataLoading(true);
		let sessionData = {
			submission_id: item?.id,
			language: language,
		};
		let response = await backendConnector.downloadSubmissionDataExcel(sessionData);
		if (response?.error) {
			setError(t("assessment.results.downloadError"));
		} else if (response?.message) {
			setMessage(response?.message);
		}
		setLoading(false);
		setDataLoading(false);
	};

	return (
		<Grid>
			<Typography variant="h6" className={classes.sectionTypography}>
				{t("assessment.results.history.title")}
			</Typography>
			<TableContainer component={Paper} className={classes.tableContainer}>
				{partialLoading && (
					<Grid className={classes.loaderContainer}>
						<DotFalling />
					</Grid>
				)}
				<Table className={partialLoading ? classes.loadingTable : ""}>
					<TableHead>
						<TableRow>
							<TableCell className={classes.tableHeader}>
								{t("assessment.results.history.table.name")}
							</TableCell>
							<TableCell className={classes.tableHeader}>
								{t("assessment.results.history.table.startDate")}
							</TableCell>
							<TableCell className={classes.tableHeader}>
								{t("assessment.results.history.table.submissionDate")}
							</TableCell>
							<TableCell className={classes.tableHeader}>
								{t("assessment.results.history.table.progress")}
							</TableCell>
							<TableCell className={classes.tableHeader}>
								{t("assessment.results.history.table.status")}
							</TableCell>
							<TableCell className={classes.tableHeader}>
								{t("assessment.results.history.table.actions")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{historyResult.length === 0 ? (
							<TableRow>
								<TableCell colSpan={6} align="center">
									<Typography variant="body2" color="textSecondary">
										{t("assessment.results.history.table.none")}
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							historyResult.map((item) => (
								<TableRow key={item.id}>
									<TableCell>{item.name ? item.name : "-"}</TableCell>
									<TableCell>{item.date}</TableCell>
									<TableCell>{item.submission_date}</TableCell>
									<TableCell className={classes.progressCell}>
										<LinearProgress
											variant="determinate"
											value={item.completion}
											className={classes.linerProgress}
										/>
										<Typography className={classes.progressValue} component="span" variant="body2">
											{Math.trunc(item.completion)}%
										</Typography>
									</TableCell>
									<TableCell>
										<Typography className={classes.statusText} component="span" variant="body2">
											{t(`assessment.form.status.${getStatusKey(item.status)}`)}
										</Typography>
									</TableCell>
									<TableCell>
										<Grid className={classes.actionContainor}>
											<Button
												size="small"
												variant="outlined"
												color="secondary"
												className={classes.actionButton}
												startIcon={<VisibilityIcon />}
												disabled={!isCompleted(item.status)}
												onClick={() =>
													isCompleted(item.status) ? handleViewAnswers(item) : null
												}
											>
												{t("assessment.results.history.actions.view")}
											</Button>
											<Button
												size="small"
												variant="contained"
												disabled={!isCompleted(item.status)}
												className={
													isCompleted(item.status)
														? classes.activeActionButton
														: classes.inactiveActionButton
												}
												startIcon={<BarChartIcon />}
												onClick={() =>
													isCompleted(item.status) ? handleOpenDashboard(item) : null
												}
											>
												{t("assessment.results.history.actions.dashboard")}
											</Button>
											<Button
												size="small"
												variant="contained"
												disabled={!isCompleted(item.status)}
												className={
													isCompleted(item.status)
														? classes.activeActionButton
														: classes.inactiveActionButton
												}
												startIcon={<GetAppIcon />}
												onClick={() =>
													isCompleted(item.status) ? downloadSubmissionDataExcel(item) : null
												}
											>
												{t("common.buttons.download")}
											</Button>
											<Button
												size="small"
												variant="outlined"
												className={classes.actionButton}
												color="secondary"
												startIcon={<EditIcon />}
												onClick={() => handleEdit(item)}
											>
												{t("assessment.results.history.actions.update")}
											</Button>
											<Button
												size="small"
												className={classes.actionButton}
												variant="outlined"
												color="secondary"
												startIcon={<AssignmentTurnedInIcon />}
												onClick={() => openRenameDialog(item)}
											>
												{t("assessment.results.history.actions.rename")}
											</Button>
											<Button
												size="small"
												className={classes.actionButton}
												variant="outlined"
												color="secondary"
												startIcon={<DeleteIcon />}
												onClick={() => handleDelete(item)}
											>
												{t("assessment.results.history.actions.delete")}
											</Button>
										</Grid>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Grid>
	);
}
